/**
 * ResponsabileDashboard - Dashboard principale per Responsabile
 * Il Diario di Bordo - SaaS
 * 
 * FASE 5: Dashboard completa con:
 * - Wizard setup (se non completato)
 * - Overview centri
 * - Statistiche aggregate
 * - Quick actions
 * - Documenti da firmare
 * - Banner cancellazione in corso (se status = pending_deletion)
 * 
 * FASE 3: Aggiunto blocco step se 2FA non configurata
 * - Gli step dopo "Verifica Email" richiedono 2FA
 * - Banner avviso se 2FA mancante
 * - Sync automatico stato 2FA con backend
 * 
 * FASE 6B: Sistema trasferimento centri
 * - Step scelta "Crea Centro" vs "Ricevi Centro Esistente"
 * - Generazione codice di ricezione
 * - Step invita coordinatore dopo creazione centro
 * - Sezione richieste trasferimento in arrivo
 * - Banner per Organization in attesa di trasferimento
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import DOMPurify from 'dompurify'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { organizationOnboardingApi, organizationApi } from '../../services/organizationApi'
import { Card, PageLoader, Badge } from '../../components/ui'

import {
  Building2,
  Users,
  Baby,
  FileText,
  CheckCircle,
  AlertTriangle,
  Clock,
  Plus,
  ArrowRight,
  Settings,
  Shield,
  Receipt,
  TrendingUp,
  Calendar,
  Mail,
  MapPin,
  Phone,
  Loader2,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  AlertCircle,
  RefreshCw,
  FileCheck,
  Trash2,
  Eye,
  Lock,
  Download,
  FileSignature,
  // FASE 6B: Nuove icone
  ArrowRightLeft,
  Copy,
  Send,
  UserPlus,
  Inbox,
  X,
  Check,
  Key
} from 'lucide-react'

// ============================================
// CONTRACT DOCUMENT CARD COMPONENT
// Permette di visualizzare il contenuto dei documenti
// Con scroll tracking per lettura obbligatoria
// ============================================

const TYPE_DISPLAY_NAMES = {
  tos: 'Termini di Servizio',
  terms: 'Termini di Servizio',
  privacy: 'Informativa Privacy',
  dpa: 'Data Processing Agreement',
  cookie: 'Cookie Policy',
  conduct_code: 'Codice di Condotta',
  liability_waiver: 'Liberatoria Responsabilità',
  insurance: 'Polizza Assicurativa',
  parental_consent_template: 'Modello Consenso Genitori',
  other: 'Altro'
}

function ContractDocumentCard({ doc, isSelected, onToggle }) {
  const [expanded, setExpanded] = useState(false)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const contentRef = useRef(null)
  
  const typeDisplay = TYPE_DISPLAY_NAMES[doc.type] || doc.type
  const hasContent = doc.content?.body || doc.content?.externalUrl || doc.pdf?.fileId
  const hasInlineContent = !!doc.content?.body
  
  const requiresFullRead = doc.requiresFullRead
  const canAccept = !requiresFullRead || hasScrolledToBottom
  
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    const progress = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100)
    setScrollProgress(Math.min(progress, 100))
    
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToBottom(true)
    }
  }, [])
  
  const handleExternalLinkClick = () => {
    setHasScrolledToBottom(true)
  }
  
  useEffect(() => {
    if (expanded && contentRef.current) {
      const { scrollHeight, clientHeight } = contentRef.current
      if (scrollHeight <= clientHeight) {
        setHasScrolledToBottom(true)
        setScrollProgress(100)
      }
    }
  }, [expanded])
  
  const handleCardClick = () => {
    setExpanded(!expanded)
  }
  
  return (
    <div className={`border rounded-lg overflow-hidden transition-colors ${
      isSelected 
        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
    }`}>
      <div 
        className="flex items-start gap-3 p-4 cursor-pointer select-none"
        onClick={handleCardClick}
      >
        <div className="flex-shrink-0 mt-1">
          {isSelected ? (
            <div className="h-5 w-5 rounded bg-primary-500 flex items-center justify-center">
              <CheckCircle className="h-4 w-4 text-white" />
            </div>
          ) : (
            <div className={`h-5 w-5 rounded border-2 ${
              canAccept 
                ? 'border-gray-300 dark:border-gray-600' 
                : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800'
            }`} />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-white">
              {doc.title || typeDisplay}
            </span>
            <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
              v{doc.version}
            </span>
            {requiresFullRead && (
              <span className={`text-xs px-2 py-0.5 rounded ${
                hasScrolledToBottom 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
              }`}>
                {hasScrolledToBottom ? '✓ Letto' : 'Lettura obbligatoria'}
              </span>
            )}
            {isSelected && (
              <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                ✓ Accettato
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {doc.content?.summary || `Documento ${typeDisplay}`}
          </p>
          
          {!isSelected && (
            <p className="text-xs mt-2 flex items-center gap-1">
              {requiresFullRead && !hasScrolledToBottom ? (
                <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {expanded ? 'Scorri fino in fondo per poter accettare' : 'Clicca per aprire e leggere il documento'}
                </span>
              ) : (
                <span className="text-primary-600 dark:text-primary-400 flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  {expanded ? 'Scorri per visualizzare, poi accetta in fondo' : 'Clicca per visualizzare il documento'}
                </span>
              )}
            </p>
          )}
          
          {doc.signatureDeadline && (
            <p className="text-xs text-orange-600 dark:text-orange-400 mt-2 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Scadenza: {new Date(doc.signatureDeadline).toLocaleDateString('it-IT')}
            </p>
          )}
        </div>
        
        <div className="flex-shrink-0 p-1 text-gray-400">
          {expanded ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </div>
      </div>
      
      {expanded && hasContent && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex flex-wrap items-center gap-2 p-3 border-b border-gray-200 dark:border-gray-700">
            {doc.content?.externalUrl && (
              <a
                href={doc.content.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-white dark:bg-gray-800 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                onClick={handleExternalLinkClick}
              >
                <ExternalLink className="h-4 w-4" />
                Apri link esterno
              </a>
            )}
            {doc.pdf?.fileId && (
              <a
                href={`/api/legal/pdf/${doc.pdf.fileId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={handleExternalLinkClick}
              >
                <Download className="h-4 w-4" />
                Scarica PDF
              </a>
            )}
            
            {hasInlineContent && requiresFullRead && (
              <div className="flex items-center gap-2 ml-auto text-sm">
                <span className={hasScrolledToBottom ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}>
                  {hasScrolledToBottom ? '✓ Completato' : `Lettura: ${scrollProgress}%`}
                </span>
                {!hasScrolledToBottom && (
                  <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary-500 transition-all duration-300"
                      style={{ width: `${scrollProgress}%` }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          
          {doc.content?.body && (
            <div 
              ref={contentRef}
              onScroll={handleScroll}
              className="p-4 max-h-80 overflow-y-auto"
            >
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {doc.content.format === 'html' ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.content.body) }} />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-sans bg-transparent p-0 m-0">
                    {doc.content.body}
                  </pre>
                )}
              </div>
              
              {requiresFullRead && !hasScrolledToBottom && (
                <div className="mt-4 pt-4 border-t border-dashed border-gray-300 dark:border-gray-600 text-center">
                  <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center justify-center gap-2">
                    <ChevronDown className="h-4 w-4 animate-bounce" />
                    Scorri fino in fondo per continuare
                    <ChevronDown className="h-4 w-4 animate-bounce" />
                  </p>
                </div>
              )}
            </div>
          )}
          
          {!doc.content?.body && (doc.content?.externalUrl || doc.pdf?.fileId) && (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <FileSignature className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {doc.pdf?.fileId 
                  ? 'Scarica il PDF per visualizzare il documento completo'
                  : 'Clicca sul link esterno per visualizzare il documento completo'
                }
              </p>
              {requiresFullRead && !hasScrolledToBottom && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  Apri il documento per poterlo selezionare
                </p>
              )}
            </div>
          )}
          
          {canAccept && !isSelected && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <label 
                className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => onToggle(e.target.checked)}
                  className="h-5 w-5 rounded border-green-400 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm font-medium text-green-800 dark:text-green-300">
                  Ho letto e accetto "{doc.title || typeDisplay}"
                </span>
              </label>
            </div>
          )}
          
          {isSelected && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Documento accettato</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggle(false)
                  }}
                  className="ml-auto text-sm text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 underline"
                >
                  Rimuovi selezione
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================
// INCOMING TRANSFER REQUEST CARD
// FASE 6B: Card per richieste in arrivo
// ============================================

function IncomingTransferCard({ request, onApprove, onReject, loading }) {
  const [showRejectReason, setShowRejectReason] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-blue-200 dark:border-blue-800 p-5">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
          <Inbox className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-gray-900 dark:text-white">
              {request.centerSnapshot?.name || 'Centro'}
            </h4>
            <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
              In arrivo
            </span>
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Da: <strong>{request.fromOrganization?.name}</strong>
          </p>
          
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <Baby className="h-4 w-4" />
              {request.centerSnapshot?.minoriCount || 0} minori
            </span>
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {request.centerSnapshot?.educatoriCount || 0} educatori
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {request.daysRemaining} giorni rimanenti
            </span>
          </div>
          
          {request.notes && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">
              "{request.notes}"
            </p>
          )}
          
          {!showRejectReason ? (
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => onApprove(request.id)}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Approva
              </button>
              <button
                onClick={() => setShowRejectReason(true)}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                Rifiuta
              </button>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Motivo del rifiuto (opzionale)"
                className="input"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => onReject(request.id, rejectReason)}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Conferma Rifiuto
                </button>
                <button
                  onClick={() => {
                    setShowRejectReason(false)
                    setRejectReason('')
                  }}
                  className="btn-secondary"
                >
                  Annulla
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================
// SETUP WIZARD COMPONENT
// FASE 3: Aggiornato con blocco 2FA
// FASE 6B: Aggiunto step scelta centro e invito coordinatore
// ============================================

function SetupWizard({ status, onRefresh }) {
  const [loading, setLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(null)
  const [syncing2FA, setSyncing2FA] = useState(false)
  const navigate = useNavigate()
  const { showSuccess, showError, showWarning } = useNotification()
  
  const { organization, progress, user, requires2FA } = status
  const checklist = organization?.setupChecklist || {}
  
  // FASE 3: Verifica se l'utente ha 2FA configurata
  const has2FA = user?.twoFactorEnabled || checklist.twoFactorAuth
  const emailVerified = organization?.emailVerified
  
  // FASE 6B: Stato per scelta centro
  const [centerChoice, setCenterChoice] = useState(null) // 'create' | 'receive'
  const [generatedCode, setGeneratedCode] = useState(null)
  const [codeCopied, setCodeCopied] = useState(false)
  
  // FASE 6B: Stato per invito coordinatore
  const [coordinatorForm, setCoordinatorForm] = useState({
    nome: '',
    cognome: '',
    email: '',
    requirePin: true // Default: PIN abilitato per sicurezza
  })
  const [createdCenterId, setCreatedCenterId] = useState(null)
  const [coordinatorPinResult, setCoordinatorPinResult] = useState(null) // Risultato con PIN
  const [pinCopied, setPinCopied] = useState(false)
  
  // FASE 3: Sync stato 2FA quando il componente viene montato
  useEffect(() => {
    if (emailVerified && !checklist.twoFactorAuth) {
      sync2FAStatus()
    }
  }, [emailVerified])
  
  const sync2FAStatus = async () => {
    try {
      setSyncing2FA(true)
      const res = await organizationOnboardingApi.sync2FAStatus()
      
      if (res.data.data?.twoFactorEnabled && !checklist.twoFactorAuth) {
        showSuccess('2FA configurata correttamente!')
        onRefresh()
      }
    } catch (err) {
      console.error('Error syncing 2FA status:', err)
    } finally {
      setSyncing2FA(false)
    }
  }
  
  const steps = [
    {
      id: 'verify-email',
      label: 'Verifica Email',
      description: 'Conferma il tuo indirizzo email',
      completed: organization?.emailVerified,
      icon: Mail,
      action: () => navigate('/verify-email-pending'),
      requires2FA: false
    },
    {
      id: 'setup-2fa',
      label: 'Autenticazione 2FA',
      description: 'Configura l\'autenticazione a due fattori (obbligatorio)',
      completed: checklist.twoFactorAuth,
      icon: Shield,
      action: () => navigate('/setup-2fa'),
      requires2FA: false,
      isRequired: true
    },
    {
      id: 'fiscal-data',
      label: 'Dati Fiscali',
      description: 'Email, Ragione Sociale, P.IVA, PEC',
      completed: checklist.fiscalData,
      icon: Receipt,
      action: () => setCurrentStep('fiscal'),
      requires2FA: true
    },
    {
      id: 'legal-address',
      label: 'Sede Legale',
      description: 'Indirizzo sede legale',
      completed: checklist.legalAddress,
      icon: MapPin,
      action: () => setCurrentStep('address'),
      requires2FA: true
    },
    {
      id: 'contracts',
      label: 'Documenti Legali',
      description: 'Accetta termini e DPA',
      completed: checklist.contracts,
      icon: FileCheck,
      action: () => setCurrentStep('contracts'),
      requires2FA: true
    },
    {
      id: 'first-center',
      label: 'Primo Centro',
      description: 'Crea o ricevi il tuo primo centro',
      completed: checklist.firstCenter || organization?.awaitingIncomingTransfer,
      icon: Building2,
      action: () => setCurrentStep('center-choice'), // FASE 6B: Prima mostra la scelta
      requires2FA: true
    }
  ]
  
  // Form states
  const [fiscalForm, setFiscalForm] = useState({
    email: '',
    ragioneSociale: '',
    partitaIva: '',
    codiceFiscale: '',
    pec: '',
    sdi: ''
  })
  
  const [addressForm, setAddressForm] = useState({
    street: '',
    city: '',
    province: '',
    postalCode: ''
  })
  
  const [centerForm, setCenterForm] = useState({
    name: '',
    entityType: 'cooperativa',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      province: '',
      postalCode: ''
    }
  })
  
  const [pendingContracts, setPendingContracts] = useState([])
  const [selectedContracts, setSelectedContracts] = useState([])
  
  useEffect(() => {
    if (currentStep === 'contracts') {
      loadPendingContracts()
    }
  }, [currentStep])
  
  const loadPendingContracts = async () => {
    try {
      const res = await organizationOnboardingApi.getPendingContracts()
      setPendingContracts(res.data.data?.pending || [])
    } catch (err) {
      console.error('Error loading contracts:', err)
      if (err.response?.data?.code === '2FA_REQUIRED') {
        showWarning('Configura prima l\'autenticazione a due fattori')
        navigate('/setup-2fa')
      }
    }
  }
  
  const handleStepClick = (step) => {
    if (step.requires2FA && !has2FA) {
      showWarning('Devi prima configurare l\'autenticazione a due fattori (2FA)')
      navigate('/setup-2fa')
      return
    }
    step.action()
  }
  
  // Submit handlers
  const handleSubmitFiscal = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await organizationOnboardingApi.completeFiscal(fiscalForm)
      showSuccess('Dati fiscali salvati')
      setCurrentStep(null)
      onRefresh()
    } catch (err) {
      if (err.response?.data?.code === '2FA_REQUIRED') {
        showWarning('Configura prima l\'autenticazione a due fattori')
        navigate('/setup-2fa')
      } else {
        showError(err.response?.data?.message || 'Errore nel salvataggio')
      }
    } finally {
      setLoading(false)
    }
  }
  
  const handleSubmitAddress = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await organizationOnboardingApi.completeAddress(addressForm)
      showSuccess('Indirizzo salvato')
      setCurrentStep(null)
      onRefresh()
    } catch (err) {
      if (err.response?.data?.code === '2FA_REQUIRED') {
        showWarning('Configura prima l\'autenticazione a due fattori')
        navigate('/setup-2fa')
      } else {
        showError(err.response?.data?.message || 'Errore nel salvataggio')
      }
    } finally {
      setLoading(false)
    }
  }
  
  const handleSubmitContracts = async () => {
    // FIX: Se non ci sono documenti pendenti, invia array vuoto per auto-completare lo step
    if (pendingContracts.length > 0 && selectedContracts.length === 0) {
      showError('Seleziona almeno un documento')
      return
    }
    setLoading(true)
    try {
      await organizationOnboardingApi.acceptContracts(
        pendingContracts.length > 0 ? selectedContracts : []
      )
      showSuccess(pendingContracts.length > 0 ? 'Documenti accettati' : 'Step completato')
      setCurrentStep(null)
      onRefresh()
    } catch (err) {
      if (err.response?.data?.code === '2FA_REQUIRED') {
        showWarning('Configura prima l\'autenticazione a due fattori')
        navigate('/setup-2fa')
      } else {
        showError(err.response?.data?.message || 'Errore nell\'accettazione')
      }
    } finally {
      setLoading(false)
    }
  }
  
  // FASE 6B: Crea centro e passa a invito coordinatore
  const handleSubmitCenter = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const res = await organizationOnboardingApi.createFirstCenter(centerForm)
      const centerId = res.data.data?.tenant?.id
      setCreatedCenterId(centerId)
      showSuccess('Centro creato!')
      setCurrentStep('invite-coordinator') // Passa allo step invito
    } catch (err) {
      if (err.response?.data?.code === '2FA_REQUIRED') {
        showWarning('Configura prima l\'autenticazione a due fattori')
        navigate('/setup-2fa')
      } else {
        showError(err.response?.data?.message || 'Errore nella creazione')
      }
    } finally {
      setLoading(false)
    }
  }
  
  // FASE 6B: Genera codice di ricezione
  const handleGenerateTransferCode = async () => {
    setLoading(true)
    try {
      const res = await organizationApi.generateTransferCode()
      setGeneratedCode(res.data.transferCode)
      showSuccess('Codice generato!')
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nella generazione del codice')
    } finally {
      setLoading(false)
    }
  }
  
  // FASE 6B: Copia codice negli appunti
  const handleCopyCode = () => {
    if (generatedCode?.code) {
      navigator.clipboard.writeText(generatedCode.code)
      setCodeCopied(true)
      setTimeout(() => setCodeCopied(false), 2000)
    }
  }
  
  // FASE 6B: Completa setup in attesa di trasferimento
  const handleCompleteAwaitingTransfer = async () => {
    setLoading(true)
    try {
      await organizationOnboardingApi.completeAwaitingTransfer()
      showSuccess('Configurazione completata! Attendi il trasferimento del centro.')
      setCurrentStep(null)
      onRefresh()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel completamento')
    } finally {
      setLoading(false)
    }
  }
  
  // FASE 6B: Invita coordinatore
  const handleInviteCoordinator = async (e) => {
    e.preventDefault()
    if (!createdCenterId) {
      showError('Centro non trovato')
      return
    }
    setLoading(true)
    try {
      const response = await organizationApi.inviteCoordinator(createdCenterId, coordinatorForm)
      
      // Se c'è un PIN, mostralo invece di chiudere subito
      if (response.data.pin) {
        setCoordinatorPinResult({
          pin: response.data.pin,
          nome: coordinatorForm.nome,
          cognome: coordinatorForm.cognome,
          email: coordinatorForm.email,
          message: response.data.pinMessage
        })
        showSuccess('Invito inviato! Comunica il PIN al coordinatore.')
      } else {
        showSuccess('Invito inviato!')
        setCurrentStep(null)
        onRefresh()
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nell\'invio dell\'invito')
    } finally {
      setLoading(false)
    }
  }
  
  // FASE 6B: Copia PIN coordinatore
  const handleCopyCoordinatorPin = () => {
    if (coordinatorPinResult?.pin) {
      navigator.clipboard.writeText(coordinatorPinResult.pin)
      setPinCopied(true)
      setTimeout(() => setPinCopied(false), 2000)
    }
  }
  
  // FASE 6B: Chiudi modale PIN e completa
  const handleClosePinAndComplete = () => {
    setCoordinatorPinResult(null)
    setCurrentStep(null)
    onRefresh()
  }
  
  // FASE 6B: Salta invito coordinatore
  const handleSkipCoordinatorInvite = () => {
    setCurrentStep(null)
    onRefresh()
  }
  
  // Render step form
  const renderStepForm = () => {
    switch (currentStep) {
      case 'fiscal':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Dati Fiscali
            </h3>
            <form onSubmit={handleSubmitFiscal} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Organizzazione *
                  </label>
                  <input
                    type="email"
                    value={fiscalForm.email}
                    onChange={(e) => setFiscalForm({ ...fiscalForm, email: e.target.value })}
                    placeholder="info@organizzazione.it"
                    required
                    className="input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email ufficiale dell'organizzazione</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ragione Sociale *
                  </label>
                  <input
                    type="text"
                    value={fiscalForm.ragioneSociale}
                    onChange={(e) => setFiscalForm({ ...fiscalForm, ragioneSociale: e.target.value })}
                    placeholder="Cooperativa Sociale Aurora Soc. Coop."
                    required
                    minLength={3}
                    maxLength={150}
                    className="input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Partita IVA
                  </label>
                  <input
                    type="text"
                    value={fiscalForm.partitaIva}
                    onChange={(e) => setFiscalForm({ ...fiscalForm, partitaIva: e.target.value.replace(/\D/g, '') })}
                    placeholder="12345678901"
                    maxLength={11}
                    className="input"
                  />
                  <p className="text-xs text-gray-500 mt-1">11 cifre (obbligatorio se non CF)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Codice Fiscale
                  </label>
                  <input
                    type="text"
                    value={fiscalForm.codiceFiscale}
                    onChange={(e) => setFiscalForm({ ...fiscalForm, codiceFiscale: e.target.value.toUpperCase() })}
                    placeholder="12345678901 o RSSMRA80A01H501Z"
                    maxLength={16}
                    className="input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Obbligatorio se non P.IVA</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    PEC
                  </label>
                  <input
                    type="email"
                    value={fiscalForm.pec}
                    onChange={(e) => setFiscalForm({ ...fiscalForm, pec: e.target.value })}
                    placeholder="organizzazione@pec.it"
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Codice SDI
                  </label>
                  <input
                    type="text"
                    value={fiscalForm.sdi}
                    onChange={(e) => setFiscalForm({ ...fiscalForm, sdi: e.target.value.toUpperCase() })}
                    placeholder="XXXXXXX"
                    maxLength={7}
                    className="input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Per fatturazione elettronica</p>
                </div>
              </div>
              
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  <strong>Nota:</strong> È obbligatorio inserire almeno Partita IVA o Codice Fiscale.
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setCurrentStep(null)}
                  className="btn-secondary"
                >
                  Annulla
                </button>
                <button 
                  type="submit" 
                  disabled={loading || !fiscalForm.email || !fiscalForm.ragioneSociale || (!fiscalForm.partitaIva && !fiscalForm.codiceFiscale)} 
                  className="btn-primary"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        )
        
      case 'address':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Sede Legale
            </h3>
            <form onSubmit={handleSubmitAddress} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Indirizzo *
                </label>
                <input
                  type="text"
                  value={addressForm.street}
                  onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                  placeholder="Via Roma, 1"
                  required
                  className="input"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Città *
                  </label>
                  <input
                    type="text"
                    value={addressForm.city}
                    onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                    placeholder="Milano"
                    required
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Provincia *
                  </label>
                  <input
                    type="text"
                    value={addressForm.province}
                    onChange={(e) => setAddressForm({ ...addressForm, province: e.target.value.toUpperCase() })}
                    placeholder="MI"
                    maxLength={2}
                    required
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    CAP *
                  </label>
                  <input
                    type="text"
                    value={addressForm.postalCode}
                    onChange={(e) => setAddressForm({ ...addressForm, postalCode: e.target.value })}
                    placeholder="20100"
                    maxLength={5}
                    required
                    className="input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setCurrentStep(null)}
                  className="btn-secondary"
                >
                  Annulla
                </button>
                <button type="submit" disabled={loading} className="btn-primary">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        )
        
      case 'contracts':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Documenti Legali da Accettare
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Leggi attentamente ogni documento prima di accettarlo.
            </p>
            {pendingContracts.length === 0 ? (
              <div className="text-center py-8">
                <FileCheck className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400 font-medium">
                  Tutti i documenti sono stati accettati!
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Conferma per proseguire con il setup.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingContracts.map((doc) => (
                  <ContractDocumentCard
                    key={doc._id}
                    doc={doc}
                    isSelected={selectedContracts.includes(doc._id)}
                    onToggle={(checked) => {
                      if (checked) {
                        setSelectedContracts([...selectedContracts, doc._id])
                      } else {
                        setSelectedContracts(selectedContracts.filter(id => id !== doc._id))
                      }
                    }}
                  />
                ))}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setCurrentStep(null)}
                className="btn-secondary"
              >
                Annulla
              </button>
              <button
                onClick={handleSubmitContracts}
                disabled={loading || (pendingContracts.length > 0 && selectedContracts.length === 0)}
                className="btn-primary"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  pendingContracts.length === 0 
                    ? 'Conferma e Continua' 
                    : `Accetta Selezionati (${selectedContracts.length})`
                )}
              </button>
            </div>
          </div>
        )
      
      // FASE 6B: Step scelta "Crea" o "Ricevi"
      case 'center-choice':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Come vuoi iniziare?
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Scegli se creare un nuovo centro o ricevere un centro esistente da un'altra Organization.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Opzione: Crea Nuovo Centro */}
              <button
                onClick={() => {
                  setCenterChoice('create')
                  setCurrentStep('center')
                }}
                className="p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-all text-left"
              >
                <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg w-fit mb-4">
                  <Plus className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Crea un Nuovo Centro
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Avvia un nuovo centro da zero. Potrai invitare coordinatori ed educatori.
                </p>
              </button>
              
              {/* Opzione: Ricevi Centro */}
              <button
                onClick={() => {
                  setCenterChoice('receive')
                  setCurrentStep('receive-code')
                }}
                className="p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all text-left"
              >
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg w-fit mb-4">
                  <Inbox className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Ricevi un Centro Esistente
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Genera un codice di ricezione per ricevere un centro da un'altra Organization.
                </p>
              </button>
            </div>
            
            <div className="flex justify-start pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setCurrentStep(null)}
                className="btn-secondary"
              >
                Annulla
              </button>
            </div>
          </div>
        )
      
      // FASE 6B: Step genera codice di ricezione
      case 'receive-code':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Ricevi un Centro
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Genera un codice di ricezione e comunicalo all'Organization che deve trasferirti il centro.
            </p>
            
            {!generatedCode ? (
              <div className="text-center py-8">
                <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full w-fit mx-auto mb-4">
                  <Key className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                </div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Genera il tuo Codice di Ricezione
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                  Il codice sarà valido per 30 giorni. Potrai sempre revocarlo o generarne uno nuovo.
                </p>
                <button
                  onClick={handleGenerateTransferCode}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Key className="h-4 w-4 mr-2" />
                  )}
                  Genera Codice
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Codice Generato */}
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6 text-center">
                  <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400 mx-auto mb-3" />
                  <p className="text-sm text-green-700 dark:text-green-300 mb-4">
                    Codice generato con successo!
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl font-mono font-bold text-gray-900 dark:text-white bg-white dark:bg-gray-800 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
                      {generatedCode.code}
                    </span>
                    <button
                      onClick={handleCopyCode}
                      className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                      title="Copia codice"
                    >
                      {codeCopied ? (
                        <Check className="h-5 w-5 text-green-600" />
                      ) : (
                        <Copy className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                    Scade il {new Date(generatedCode.expiresAt).toLocaleDateString('it-IT')} ({generatedCode.daysRemaining} giorni)
                  </p>
                </div>
                
                {/* Istruzioni */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Prossimi passi
                  </h4>
                  <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-2 list-decimal list-inside">
                    <li>Comunica questo codice all'Organization che deve trasferirti il centro</li>
                    <li>L'altra Organization userà il codice per inviarti la richiesta di trasferimento</li>
                    <li>Riceverai una notifica quando la richiesta arriverà</li>
                    <li>Potrai approvare o rifiutare la richiesta dalla dashboard</li>
                  </ol>
                </div>
              </div>
            )}
            
            <div className="flex justify-between pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  setGeneratedCode(null)
                  setCurrentStep('center-choice')
                }}
                className="btn-secondary"
              >
                Indietro
              </button>
              {generatedCode && (
                <button
                  onClick={handleCompleteAwaitingTransfer}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Completa e Attendi Trasferimento
                </button>
              )}
            </div>
          </div>
        )
        
      case 'center':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Crea il tuo Primo Centro
            </h3>
            <form onSubmit={handleSubmitCenter} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome Centro *
                </label>
                <input
                  type="text"
                  value={centerForm.name}
                  onChange={(e) => setCenterForm({ ...centerForm, name: e.target.value })}
                  placeholder="Centro Educativo Aurora"
                  required
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo Ente *
                </label>
                <select
                  value={centerForm.entityType}
                  onChange={(e) => setCenterForm({ ...centerForm, entityType: e.target.value })}
                  className="input"
                  required
                >
                  <option value="cooperativa">Cooperativa Sociale</option>
                  <option value="onlus">ONLUS</option>
                  <option value="privato">Privato</option>
                  <option value="pubblico">Ente Pubblico</option>
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Centro *
                  </label>
                  <input
                    type="email"
                    value={centerForm.email}
                    onChange={(e) => setCenterForm({ ...centerForm, email: e.target.value })}
                    placeholder="centro@example.com"
                    required
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Telefono
                  </label>
                  <input
                    type="tel"
                    value={centerForm.phone}
                    onChange={(e) => setCenterForm({ ...centerForm, phone: e.target.value })}
                    placeholder="+39 02 1234567"
                    className="input"
                  />
                </div>
              </div>
              
              {/* Sezione Indirizzo */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  Indirizzo del Centro
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Via/Indirizzo
                    </label>
                    <input
                      type="text"
                      value={centerForm.address.street}
                      onChange={(e) => setCenterForm({
                        ...centerForm,
                        address: { ...centerForm.address, street: e.target.value }
                      })}
                      placeholder="Via Roma, 1"
                      className="input"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Città
                      </label>
                      <input
                        type="text"
                        value={centerForm.address.city}
                        onChange={(e) => setCenterForm({
                          ...centerForm,
                          address: { ...centerForm.address, city: e.target.value }
                        })}
                        placeholder="Milano"
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Provincia
                      </label>
                      <input
                        type="text"
                        value={centerForm.address.province}
                        onChange={(e) => setCenterForm({
                          ...centerForm,
                          address: { ...centerForm.address, province: e.target.value.toUpperCase() }
                        })}
                        placeholder="MI"
                        maxLength={2}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        CAP
                      </label>
                      <input
                        type="text"
                        value={centerForm.address.postalCode}
                        onChange={(e) => setCenterForm({
                          ...centerForm,
                          address: { ...centerForm.address, postalCode: e.target.value }
                        })}
                        placeholder="20100"
                        maxLength={5}
                        className="input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setCurrentStep('center-choice')}
                  className="btn-secondary"
                >
                  Indietro
                </button>
                <button type="submit" disabled={loading} className="btn-primary">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crea Centro'}
                </button>
              </div>
            </form>
          </div>
        )
      
      // FASE 6B: Step invita coordinatore
      case 'invite-coordinator':
        // Se c'è un PIN da mostrare
        if (coordinatorPinResult) {
          return (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-800 dark:text-green-200">
                      Invito Inviato!
                    </h4>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      Email inviata a {coordinatorPinResult.email}
                    </p>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      PIN di Verifica
                    </span>
                    <button
                      onClick={handleCopyCoordinatorPin}
                      className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <Copy className="h-4 w-4" />
                      {pinCopied ? 'Copiato!' : 'Copia'}
                    </button>
                  </div>
                  <div className="text-3xl font-mono font-bold text-center tracking-widest text-gray-900 dark:text-white py-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    {coordinatorPinResult.pin}
                  </div>
                </div>
                
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <Shield className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800 dark:text-amber-200">
                      <p className="font-medium mb-1">Importante!</p>
                      <p>Comunica questo PIN a <strong>{coordinatorPinResult.nome} {coordinatorPinResult.cognome}</strong> di persona, per telefono o via chat. Senza il PIN non potrà completare la registrazione.</p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleClosePinAndComplete}
                  className="w-full btn-primary"
                >
                  Ho comunicato il PIN
                </button>
              </div>
            </div>
          )
        }
        
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Centro Creato!
              </h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Vuoi invitare subito un coordinatore per gestire il centro?
            </p>
            
            <form onSubmit={handleInviteCoordinator} className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300 mb-2">
                  <UserPlus className="h-5 w-5" />
                  <span className="font-medium">Invita il Primo Coordinatore</span>
                </div>
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  Il coordinatore potrà gestire educatori, minori e registrazioni del centro.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={coordinatorForm.nome}
                    onChange={(e) => setCoordinatorForm({ ...coordinatorForm, nome: e.target.value })}
                    required
                    placeholder="Mario"
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cognome *
                  </label>
                  <input
                    type="text"
                    value={coordinatorForm.cognome}
                    onChange={(e) => setCoordinatorForm({ ...coordinatorForm, cognome: e.target.value })}
                    required
                    placeholder="Rossi"
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={coordinatorForm.email}
                    onChange={(e) => setCoordinatorForm({ ...coordinatorForm, email: e.target.value })}
                    required
                    placeholder="coordinatore@example.com"
                    className="input"
                  />
                </div>
              </div>
              
              {/* Opzione PIN */}
              <div className="flex items-start gap-2 pt-2">
                <input
                  type="checkbox"
                  id="requirePinWizard"
                  checked={coordinatorForm.requirePin}
                  onChange={(e) => setCoordinatorForm({ ...coordinatorForm, requirePin: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                />
                <label htmlFor="requirePinWizard" className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="flex items-center gap-1">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span className="font-medium">Richiedi PIN di verifica</span>
                    <span className="text-green-600">(consigliato)</span>
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 block mt-0.5">
                    Dovrai comunicare un PIN a 6 cifre al coordinatore per confermare la sua identità
                  </span>
                </label>
              </div>
              
              <div className="flex justify-between gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleSkipCoordinatorInvite}
                  className="btn-secondary"
                >
                  Salta per ora
                </button>
                <button type="submit" disabled={loading} className="btn-primary">
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Invia Invito
                </button>
              </div>
            </form>
          </div>
        )
        
      default:
        return null
    }
  }
  
  // If showing a form, render only that
  if (currentStep) {
    return renderStepForm()
  }
  
  // Main wizard view
  return (
    <div className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl p-6 border border-primary-200 dark:border-primary-800">
      {/* FASE 3: Banner avviso se 2FA non configurata ma email verificata */}
      {emailVerified && !has2FA && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-amber-800 dark:text-amber-200">
                Autenticazione a Due Fattori Richiesta
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Per motivi di sicurezza, devi configurare l'autenticazione a due fattori (2FA) prima di poter proseguire con la configurazione.
              </p>
              <button
                onClick={() => navigate('/setup-2fa')}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Shield className="h-4 w-4" />
                Configura 2FA Ora
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Completa la Configurazione
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {progress?.percentage || 0}% completato
          </p>
        </div>
        <div className="flex items-center gap-2">
          {syncing2FA && (
            <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
          )}
          <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-600 dark:bg-primary-500 transition-all duration-500"
              style={{ width: `${progress?.percentage || 0}%` }}
            />
          </div>
          <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
            {progress?.completed || 0}/{progress?.total || 6}
          </span>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {steps.map((step, index) => {
          const Icon = step.icon
          
          const previousStepIncomplete = index > 0 && !steps[index - 1].completed
          const needs2FAButMissing = step.requires2FA && !has2FA
          const isDisabled = previousStepIncomplete || needs2FAButMissing
          
          const showLock = needs2FAButMissing && !previousStepIncomplete
          
          return (
            <button
              key={step.id}
              onClick={() => handleStepClick(step)}
              disabled={isDisabled || step.completed}
              className={`
                p-4 rounded-lg text-left transition-all relative
                ${step.completed
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : isDisabled
                    ? 'bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md'
                }
              `}
            >
              {step.isRequired && !step.completed && (
                <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-red-500 text-white text-xs font-medium rounded-full">
                  Obbligatorio
                </span>
              )}
              
              {showLock && (
                <span className="absolute -top-2 -right-2 p-1 bg-amber-500 text-white rounded-full">
                  <Lock className="h-3 w-3" />
                </span>
              )}
              
              <div className="flex items-start gap-3">
                <div className={`
                  p-2 rounded-lg
                  ${step.completed
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                    : showLock
                      ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }
                `}>
                  {step.completed ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : showLock ? (
                    <Lock className="h-5 w-5" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`
                      font-medium
                      ${step.completed
                        ? 'text-green-700 dark:text-green-400'
                        : 'text-gray-900 dark:text-white'
                      }
                    `}>
                      {step.label}
                    </span>
                    {step.completed && (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {showLock 
                      ? 'Configura prima 2FA per sbloccare'
                      : step.description
                    }
                  </p>
                </div>
                {!step.completed && !isDisabled && (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ============================================
// CENTER CARD COMPONENT
// ============================================

function CenterCard({ center, onClick }) {
  const statusColors = {
    trial: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    suspended: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    pending_deletion: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    export_only: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    cancelled: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
    deleted: 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-500'
  }

  const statusLabels = {
    trial: 'Trial',
    active: 'Attivo',
    suspended: 'Sospeso',
    pending_deletion: 'In Cancellazione',
    export_only: 'Solo Esportazione',
    cancelled: 'Cancellato (legacy)',
    deleted: 'Eliminato'
  }

  const fallbackColor = 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
  const badgeColor = statusColors[center.status] || fallbackColor
  const badgeLabel = statusLabels[center.status] || 'Sconosciuto'
  
  // FASE 6B: Warning se centro senza coordinatore
  const needsCoordinator = !center.coordinatoreId || center.needsCoordinator
  
  return (
    <div
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border ${
        needsCoordinator 
          ? 'border-amber-300 dark:border-amber-700' 
          : 'border-gray-200 dark:border-gray-700'
      } p-5 hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all cursor-pointer`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Building2 className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {center.name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {center.entityType}
            </p>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeColor}`}>
          {badgeLabel}
        </span>
      </div>
      
      {/* FASE 6B: Warning coordinatore */}
      {needsCoordinator && (
        <div className="mb-4 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Nessun coordinatore</span>
        </div>
      )}
      
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {center.stats?.minori || center.usage?.minori || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Minori</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {center.stats?.educatori || center.usage?.educatori || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Educatori</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {center.stats?.coordinatori || center.usage?.coordinatori || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Coordinatori</p>
        </div>
      </div>
      
      {center.status === 'trial' && center.trialDaysRemaining !== undefined && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-blue-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {center.trialDaysRemaining} giorni rimanenti nel trial
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================
// STATS CARD COMPONENT
// ============================================

function StatsCard({ icon: Icon, label, value, subtitle, color = 'primary' }) {
  const colorClasses = {
    primary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function ResponsabileDashboard() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [status, setStatus] = useState(null)
  const [centers, setCenters] = useState([])
  const [stats, setStats] = useState(null)
  const [deletionStatus, setDeletionStatus] = useState(null)
  // FASE 6B: Richieste in arrivo
  const [incomingTransfers, setIncomingTransfers] = useState([])
  const [transferLoading, setTransferLoading] = useState(false)
  
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  const navigate = useNavigate()
  
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Carica status onboarding
      const statusRes = await organizationOnboardingApi.getStatus()
      setStatus(statusRes.data.data)
      
      // Se setup completo, carica anche centri, stats, stato cancellazione e richieste in arrivo
      if (statusRes.data.data?.organization?.setupComplete) {
        const [centersRes, statsRes, deletionRes, incomingRes] = await Promise.all([
          organizationApi.getCenters(),
          organizationApi.getStats(),
          organizationApi.getDeletionStatus().catch(() => ({ data: { data: null } })),
          organizationApi.getIncomingTransfers().catch(() => ({ data: { requests: [] } }))
        ])
        setCenters(centersRes.data.data?.centers || [])
        setStats(statsRes.data.data)
        setDeletionStatus(deletionRes.data.data)
        setIncomingTransfers(incomingRes.data.requests || [])
      }
      
    } catch (err) {
      console.error('Error loading dashboard:', err)
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }, [])
  
  useEffect(() => {
    loadData()
  }, [loadData])
  
  // FASE 6B: Approva trasferimento
  const handleApproveTransfer = async (requestId) => {
    setTransferLoading(true)
    try {
      await organizationApi.approveTransfer(requestId)
      showSuccess('Trasferimento approvato! Il centro è ora tuo.')
      loadData() // Ricarica tutto
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nell\'approvazione')
    } finally {
      setTransferLoading(false)
    }
  }
  
  // FASE 6B: Rifiuta trasferimento
  const handleRejectTransfer = async (requestId, reason) => {
    setTransferLoading(true)
    try {
      await organizationApi.rejectTransfer(requestId, reason)
      showSuccess('Richiesta rifiutata')
      loadData()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel rifiuto')
    } finally {
      setTransferLoading(false)
    }
  }
  
  if (loading) {
    return <PageLoader />
  }
  
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
            Errore nel caricamento
          </h2>
          <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
          <button onClick={loadData} className="btn-primary">
            <RefreshCw className="h-4 w-4 mr-2" />
            Riprova
          </button>
        </div>
      </div>
    )
  }
  
  const org = status?.organization
  const isSetupComplete = org?.setupComplete
  
  // FASE 6B: Organization in attesa di trasferimento
  const isAwaitingTransfer = org?.awaitingIncomingTransfer
  const activeTransferCode = org?.activeTransferCode
  
  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Benvenuto, {user?.nome}! Gestisci la tua organizzazione.
          </p>
        </div>
        
        {isSetupComplete && (
          <div className="flex gap-3">
            <Link to="/organization/settings" className="btn-secondary">
              <Settings className="h-4 w-4 mr-2" />
              Impostazioni
            </Link>
            <Link to="/organization/centers/new" className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Centro
            </Link>
          </div>
        )}
      </div>
      
      {/* Organization Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
            <Building2 className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {org?.name}
            </h2>

          </div>
          <div className="text-right">
            {org?.emailVerified ? (
              <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                <CheckCircle className="h-4 w-4" />
                Email verificata
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 text-sm">
                <AlertTriangle className="h-4 w-4" />
                Email da verificare
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* FASE 6B: Banner in attesa di trasferimento */}
      {isAwaitingTransfer && activeTransferCode && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex-shrink-0">
              <Inbox className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                In Attesa di Ricevere un Centro
              </h3>
              <p className="text-blue-700 dark:text-blue-300 mt-1">
                Il tuo codice di ricezione è attivo. Comunica questo codice all'Organization che deve trasferirti il centro.
              </p>
              <div className="flex items-center gap-3 mt-3">
                <span className="text-lg font-mono font-bold text-blue-900 dark:text-blue-100 bg-white dark:bg-gray-800 px-3 py-1 rounded border border-blue-200 dark:border-blue-700">
                  {activeTransferCode}
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(activeTransferCode)
                    showSuccess('Codice copiato!')
                  }}
                  className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg"
                >
                  <Copy className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* FASE 6B: Richieste in arrivo */}
      {incomingTransfers.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Inbox className="h-5 w-5 text-blue-600" />
            Richieste di Trasferimento in Arrivo
            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-full">
              {incomingTransfers.length}
            </span>
          </h2>
          
          <div className="space-y-4">
            {incomingTransfers.map((request) => (
              <IncomingTransferCard
                key={request.id}
                request={request}
                onApprove={handleApproveTransfer}
                onReject={handleRejectTransfer}
                loading={transferLoading}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* FASE 5: Banner Cancellazione in Corso */}
      {deletionStatus?.isPendingDeletion && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/50 rounded-lg flex-shrink-0">
              <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
                Cancellazione Programmata
              </h3>
              <p className="text-red-700 dark:text-red-300 mt-1">
                L'organizzazione verrà eliminata tra{' '}
                <strong>{deletionStatus.deletion?.daysRemaining || 0} giorni</strong>
                {deletionStatus.deletion?.scheduledAt && (
                  <> ({new Date(deletionStatus.deletion.scheduledAt).toLocaleDateString('it-IT', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })})</>
                )}
              </p>
              {deletionStatus.centersAffected > 0 && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-2">
                  ⚠️ {deletionStatus.centersAffected} centri verranno eliminati
                </p>
              )}
              <div className="flex flex-wrap gap-3 mt-4">
                <Link
                  to="/organization/settings"
                  className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                >
                  Annulla Cancellazione
                </Link>
                <Link
                  to="/organization/export"
                  className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  Esporta Dati
                </Link>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-4xl font-bold text-red-600 dark:text-red-400">
                {deletionStatus.deletion?.daysRemaining || 0}
              </div>
              <div className="text-sm text-red-600 dark:text-red-400">
                giorni
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Setup Wizard (if not complete) */}
      {!isSetupComplete && (
        <SetupWizard status={status} onRefresh={loadData} />
      )}
      
      {/* Stats (if setup complete) */}
      {isSetupComplete && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard
            icon={Building2}
            label="Centri"
            value={stats.centersCount || 0}
            subtitle={`${stats.activeCenters || 0} attivi`}
            color="primary"
          />
          <StatsCard
            icon={Baby}
            label="Minori Totali"
            value={stats.totalMinori || 0}
            color="green"
          />
          <StatsCard
            icon={Users}
            label="Educatori"
            value={stats.totalEducatori || 0}
            color="blue"
          />
          <StatsCard
            icon={FileText}
            label="Registrazioni"
            value={stats.totalEntries || 0}
            subtitle="questo mese"
            color="amber"
          />
        </div>
      )}
      
      {/* Centers Grid (if setup complete) */}
      {isSetupComplete && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              I tuoi Centri
            </h2>
            <Link
              to="/organization/centers"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm font-medium inline-flex items-center gap-1"
            >
              Vedi tutti
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          
          {centers.length === 0 ? (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-8 text-center">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Nessun centro ancora
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Crea il tuo primo centro per iniziare
              </p>
              <Link to="/organization/centers/new" className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Crea Centro
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {centers.slice(0, 6).map((center) => (
                <CenterCard
                  key={center._id}
                  center={center}
                  onClick={() => navigate(`/organization/centers/${center._id}`)}
                />
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Quick Actions */}
      {isSetupComplete && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/organization/contracts"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <FileCheck className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Documenti Legali
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Visualizza contratti firmati
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
            </div>
          </Link>
          
          <Link
            to="/organization/billing"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Receipt className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Fatturazione
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Gestisci pagamenti e fatture
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
            </div>
          </Link>
          
          <Link
            to="/organization/export"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Export Dati
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Scarica i tuoi dati (GDPR)
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
            </div>
          </Link>
        </div>
      )}
    </div>
  )
}
